/**
 * useLiveStats Hook
 * Manages SSE subscription for real-time game stats updates.
 *
 * Features:
 * - Subscribes to live stats for games with pending parlays
 * - Auto-disconnects when tab is hidden or panel unmounts
 * - Auto-reconnects on visibility change or connection loss
 * - Only subscribes when there are live games to track
 *
 * Platform Notes:
 * - Web: Uses native EventSource API
 * - Mobile: Requires EventSource polyfill (e.g., react-native-sse) or polling fallback
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LeagueKey } from '../league';
import { fuzzyMatchPlayer, isPlayerStatsDNP } from '../playerNameUtils';
import { canonicalizeStatType } from '../statType';

// =============================================================================
// Types
// =============================================================================

/** Player stats from box score */
export interface PlayerBoxStats {
  playerId: number;
  playerName: string;
  teamId?: number;
  teamName?: string;
  pts: number;
  reb: number;
  ast: number;
  fg3m: number;
  fg3a: number;
  fgm: number;
  fga: number;
  ftm: number;
  fta: number;
  blk: number;
  stl: number;
  turnover: number;
  pf: number;
  min: string;
  pra: number;
  pr: number;
  pa: number;
  ra: number;
  bs: number;
}

/** Full box score data for a game (exposed via getFullBoxScore) */
export interface FullBoxScoreData {
  homeTeam: string;
  awayTeam: string;
  homeTeamId?: number;
  awayTeamId?: number;
  homeScore: number;
  awayScore: number;
  quarter: number | null;
  timeRemaining: string | null;
  gameStatus: GameStatus;
  players: Record<number, PlayerBoxStats>;
  quarterScores: QuarterScore[];
  lastUpdated: number;
}

/** Quarter score from ESPN */
export interface QuarterScore {
  period: number; // 1, 2, 3, 4, or 5+ for OT
  homeScore: number;
  awayScore: number;
}

/** SSE message from server */
interface SSEMessage {
  type?: 'connected' | 'heartbeat' | 'settlement_complete';
  gameId?: string; // Odds API game ID (hex string)
  stats?: ServerGameStats;
  parlaysSettled?: number; // Number of parlays settled (for settlement_complete)
  legsSettled?: number; // Number of legs settled (for settlement_complete)
  timestamp: number;
}

/** Tracked parlay (minimal interface needed for this hook) */
export interface TrackedParlay {
  result: string; // a BetStatus (betResult.ts)
  legs: Array<{
    settled?: boolean;
    hit?: boolean | null;
    gameId?: string | null;
  }>;
}

/**
 * Interpreted game state. 'unknown' covers statuses we cannot classify, such as a
 * postponement or a failed status fetch, so callers never mistake one for live.
 */
export type GameStatus = 'pre-game' | 'live' | 'final' | 'unknown';

/** Live leg stats for progress bars */
export interface LiveLegStats {
  currentStat: number;
  quarter: number | null;
  timeRemaining: string | null;
  gameStatus: GameStatus;
}

/** Live game scores for totals */
export interface LiveGameScores {
  homeScore: number;
  awayScore: number;
  quarter: number | null;
  timeRemaining: string | null;
  gameStatus: GameStatus;
  /** BDL abbreviation for home team — authoritative source for score-to-team mapping */
  homeTeam?: string;
  /** BDL abbreviation for away team — authoritative source for score-to-team mapping */
  awayTeam?: string;
}

export interface UseLiveStatsOptions {
  parlays: TrackedParlay[];
  baseUrl: string; // For constructing SSE endpoint URL
  onSettlementComplete?: () => void;
  /**
   * Reports SSE handling errors (post-parse). Platform wrappers should pass
   * their `logError` from errorTracking so failures reach Sentry instead of
   * being silently swallowed.
   */
  onError?: (error: Error, context?: Record<string, unknown>) => void;
}

/** Hook return type */
export interface UseLiveStatsResult {
  /** Get live stats for a player prop leg */
  getPlayerLiveStats: (
    gameId: string | null,
    playerId: number | null,
    playerName: string | null,
    statType: string,
  ) => LiveLegStats | null;
  /** Get live game scores for total props */
  getGameScores: (gameId: string | null) => LiveGameScores | null;
  /** Get quarter-by-quarter scores for a game (from ESPN) */
  getQuarterScores: (gameId: string | null) => QuarterScore[] | null;
  /** Check if a game has stats data (live OR final - for progress bars) */
  hasLiveData: (gameId: string | null) => boolean;
  /** Check if a game is currently live (for LIVE badge - excludes final games) */
  isGameLive: (gameId: string | null) => boolean;
  /** Check if a game is final (ended but may not be settled yet) */
  isGameFinal: (gameId: string | null) => boolean;
  /** Check if a leg can be removed (game confirmed pre-game, safe default = false) */
  canRemoveLeg: (gameId: string | null) => boolean;
  /** Check if a player DNP'd (game live/final but player not in box score). Returns null if no data. */
  isPlayerDNP: (
    gameId: string | null,
    playerId: number | null,
    playerName: string | null,
  ) => boolean | null;
  /** Get full box score data for a game (all players, scores, quarter scores) */
  getFullBoxScore: (gameId: string | null) => FullBoxScoreData | null;
  /** Whether SSE is currently connected */
  isConnected: boolean;
  /** Number of games being tracked (subscribed to) */
  trackedGamesCount: number;
  /** Number of games with actual live data (in progress) */
  liveGamesCount: number;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Parse game status from BallDontLie format to our format
 * BallDontLie status formats:
 * - Pre-game: ISO timestamp like "2026-01-19T19:30:00Z" or time like "7:00 PM ET"
 * - Live: "1st Qtr", "2nd Qtr", "Halftime", "3rd Qtr", "4th Qtr", "OT1", etc.
 * - Final: "Final"
 */
/** Which id a box score is fetched by: a tracked leg's Odds API id, or the game picker's BDL id. */
export type GameIdSource = 'odds_api' | 'bdl';

/** The live-stats path for a game. A BDL id carries its league: BDL ids belong to one league's API. */
export function boxScoreStatsPath(
  gameId: string,
  source: GameIdSource,
  leagueKey: LeagueKey,
): string {
  return source === 'bdl'
    ? `/api/live-stats/game/${gameId}?league=${encodeURIComponent(leagueKey)}`
    : `/api/live-stats/${gameId}`;
}

/**
 * Statuses that mean the game is actually in progress. Matched positively, because
 * the alternative is guessing: this used to return 'live' for anything it did not
 * recognise, so 'postponed', 'Suspended', 'Delayed' and the literal 'unknown' that
 * liveStatsRoutes writes on a failed fetch all rendered as live games.
 */
const LIVE_STATUS =
  /qtr|quarter|halftime|half\b|overtime|\bot\d*\b|\bq[1-4]\b|\b\d(?:st|nd|rd|th)\b|in progress|period|end of/i;

export function parseGameStatus(status: string): GameStatus {
  if (!status) return 'pre-game';
  // Case-insensitive "Final" check (BDL may return "Final", "final", etc.)
  if (/^final/i.test(status)) return 'final';
  // ISO timestamp format (pre-game): starts with year like "2026-" or "2025-"
  if (/^\d{4}-\d{2}-\d{2}/.test(status)) return 'pre-game';
  // Time format (pre-game): contains AM/PM
  if (status.includes('PM') || status.includes('AM')) return 'pre-game';
  // Stale-game detection is handled server-side (ESPN + 5h elapsed check)
  if (LIVE_STATUS.test(status)) return 'live';
  // Anything else is a state we cannot interpret, so say so rather than assume.
  // Every consumer tests for a specific state, so 'unknown' shows no live badge, no
  // progress bar, and does not count as confirmed pre-game for leg removal.
  return 'unknown';
}

/**
 * Find player stats by name (fuzzy match)
 * Uses shared fuzzyMatchPlayerName from shared/playerNameUtils.ts
 */
export function findPlayerByName(
  players: Record<number, PlayerBoxStats>,
  targetName: string,
): PlayerBoxStats | null {
  for (const player of Object.values(players)) {
    if (fuzzyMatchPlayer(targetName, player.playerName)) {
      return player;
    }
  }
  return null;
}

/** A player's live value for a stat type in any spelling (StatKey, alias, market key); 0 if none. */
export function getStatValue(
  player: ServerGameStats['players'][number] | undefined,
  statType: string,
): number {
  if (!player) return 0;

  switch (canonicalizeStatType(statType)) {
    case 'pts':
      return player.pts;
    case 'reb':
      return player.reb;
    case 'ast':
      return player.ast;
    case 'threes':
      return player.fg3m;
    case 'blk':
      return player.blk;
    case 'stl':
      return player.stl;
    case 'turnover':
      return player.turnover;
    case 'fgm':
      return player.fgm;
    case 'ftm':
      return player.ftm;
    case 'pra':
      return player.pra;
    case 'pr':
      return player.pr;
    case 'pa':
      return player.pa;
    case 'ra':
      return player.ra;
    case 'bs':
      return player.bs;
    default:
      return 0;
  }
}

/**
 * Extract unique game IDs from parlays that need live tracking
 * Only includes games that are potentially live (pending legs, not settled)
 */
export function extractLiveGameIds(parlays: TrackedParlay[]): string[] {
  const gameIds = new Set<string>();

  for (const parlay of parlays) {
    // Only track pending parlays
    if (parlay.result !== 'pending') continue;

    for (const leg of parlay.legs) {
      // Only track unsettled legs with a game ID
      if (leg.settled || leg.hit !== null || !leg.gameId) continue;
      gameIds.add(leg.gameId);
    }
  }

  return Array.from(gameIds);
}

/**
 * True if any parlay is still awaiting settlement (result === 'pending').
 *
 * Drives the tab-focus / app-foreground catch-up refetch in the web and mobile
 * tracked-parlay hooks: the only automatic post-settlement refresh is a
 * one-shot `settlement_complete` SSE broadcast (no replay), so if a game
 * settles while the app is backgrounded that update is missed and the legs
 * render stale until reload. Only worth re-pulling when something is pending.
 */
export function hasPendingParlay(parlays: TrackedParlay[]): boolean {
  return parlays.some((p) => p.result === 'pending');
}

// =============================================================================
// Shared data callbacks (used by both web and mobile hooks)
// =============================================================================

/** Internal game stats shape from SSE */
export interface ServerGameStats {
  gameId: number;
  status: string;
  homeTeam: string;
  awayTeam: string;
  homeTeamId?: number;
  awayTeamId?: number;
  homeScore: number;
  awayScore: number;
  quarter: number | null;
  timeRemaining: string | null;
  players: Record<number, PlayerBoxStats>;
  quarterScores?: QuarterScore[];
  startTime?: string;
  lastUpdated: number;
}

/**
 * Shared data-processing callbacks for live stats.
 * Platform hooks provide the gameStats Map; this function returns all the accessors.
 * Eliminates duplication between web and mobile hooks.
 */
export function useLiveStatsCallbacks(
  gameStats: Map<string, ServerGameStats>,
): Omit<UseLiveStatsResult, 'isConnected' | 'trackedGamesCount' | 'liveGamesCount'> {
  const getPlayerLiveStats = useCallback(
    (
      gameId: string | null,
      playerId: number | null,
      playerName: string | null,
      statType: string,
    ): LiveLegStats | null => {
      if (!gameId) return null;
      if (!playerId && !playerName) return null;

      const stats = gameStats.get(gameId);
      if (!stats) return null;

      let playerStats: PlayerBoxStats | null = null;
      if (playerId) {
        playerStats = stats.players[playerId] ?? null;
      }
      if (!playerStats && playerName) {
        playerStats = findPlayerByName(stats.players, playerName);
      }
      if (!playerStats) return null;

      return {
        currentStat: getStatValue(playerStats, statType),
        quarter: stats.quarter,
        timeRemaining: stats.timeRemaining,
        gameStatus: parseGameStatus(stats.status),
      };
    },
    [gameStats],
  );

  const getGameScores = useCallback(
    (gameId: string | null): LiveGameScores | null => {
      if (!gameId) return null;
      const stats = gameStats.get(gameId);
      if (!stats) return null;

      return {
        homeScore: stats.homeScore,
        awayScore: stats.awayScore,
        quarter: stats.quarter,
        timeRemaining: stats.timeRemaining,
        gameStatus: parseGameStatus(stats.status),
        homeTeam: stats.homeTeam,
        awayTeam: stats.awayTeam,
      };
    },
    [gameStats],
  );

  const getQuarterScores = useCallback(
    (gameId: string | null): QuarterScore[] | null => {
      if (!gameId) return null;
      const stats = gameStats.get(gameId);
      if (!stats) return null;
      return stats.quarterScores && stats.quarterScores.length > 0 ? stats.quarterScores : null;
    },
    [gameStats],
  );

  const getFullBoxScore = useCallback(
    (gameId: string | null): FullBoxScoreData | null => {
      if (!gameId) return null;
      const stats = gameStats.get(gameId);
      if (!stats) return null;

      return {
        homeTeam: stats.homeTeam,
        awayTeam: stats.awayTeam,
        homeTeamId: stats.homeTeamId,
        awayTeamId: stats.awayTeamId,
        homeScore: stats.homeScore,
        awayScore: stats.awayScore,
        quarter: stats.quarter,
        timeRemaining: stats.timeRemaining,
        gameStatus: parseGameStatus(stats.status),
        players: stats.players,
        quarterScores: stats.quarterScores ?? [],
        lastUpdated: stats.lastUpdated,
      };
    },
    [gameStats],
  );

  const hasLiveData = useCallback(
    (gameId: string | null): boolean => {
      if (!gameId) return false;
      const stats = gameStats.get(gameId);
      if (!stats) return false;
      const status = parseGameStatus(stats.status);
      return status === 'live' || status === 'final';
    },
    [gameStats],
  );

  const isGameLive = useCallback(
    (gameId: string | null): boolean => {
      if (!gameId) return false;
      const stats = gameStats.get(gameId);
      if (!stats) return false;
      return parseGameStatus(stats.status) === 'live';
    },
    [gameStats],
  );

  const isGameFinal = useCallback(
    (gameId: string | null): boolean => {
      if (!gameId) return false;
      const stats = gameStats.get(gameId);
      if (!stats) return false;
      return parseGameStatus(stats.status) === 'final';
    },
    [gameStats],
  );

  const canRemoveLeg = useCallback(
    (gameId: string | null): boolean => {
      if (!gameId) return false;
      const stats = gameStats.get(gameId);
      if (!stats) return false;
      return parseGameStatus(stats.status) === 'pre-game';
    },
    [gameStats],
  );

  const isPlayerDNP = useCallback(
    (gameId: string | null, playerId: number | null, playerName: string | null): boolean | null => {
      if (!gameId) return null;
      if (!playerId && !playerName) return null;

      const stats = gameStats.get(gameId);
      if (!stats) return null;

      const gameStatus = parseGameStatus(stats.status);
      if (gameStatus === 'pre-game') return false;

      let playerStats: PlayerBoxStats | null = null;
      if (playerId) {
        playerStats = stats.players[playerId] ?? null;
      }
      if (!playerStats && playerName) {
        playerStats = findPlayerByName(stats.players, playerName);
      }

      if (!playerStats) {
        const hasBoxScoreData = Object.keys(stats.players).length > 0;
        if (!hasBoxScoreData) return null;
        if (gameStatus === 'final') return true;
        return null;
      }

      return isPlayerStatsDNP(playerStats);
    },
    [gameStats],
  );

  return {
    getPlayerLiveStats,
    getGameScores,
    getQuarterScores,
    getFullBoxScore,
    hasLiveData,
    isGameLive,
    isGameFinal,
    canRemoveLeg,
    isPlayerDNP,
  };
}

// =============================================================================
// Web Hook
// =============================================================================

export function useLiveStats(options: UseLiveStatsOptions): UseLiveStatsResult {
  const { parlays, baseUrl, onSettlementComplete, onError } = options;

  // Map uses Odds API game ID (string) as key
  const [gameStats, setGameStats] = useState<Map<string, ServerGameStats>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSettlementCompleteRef = useRef(onSettlementComplete);
  const onErrorRef = useRef(onError);

  // Keep callback refs up to date
  useEffect(() => {
    onSettlementCompleteRef.current = onSettlementComplete;
  }, [onSettlementComplete]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Extract game IDs that need live tracking
  const liveGameIds = useMemo(() => extractLiveGameIds(parlays), [parlays]);

  // Connect to SSE endpoint
  const connect = useCallback(() => {
    // Don't connect if no games to track
    if (liveGameIds.length === 0) {
      return;
    }

    // Don't connect if already connected or connecting
    const currentState = eventSourceRef.current?.readyState;
    if (currentState === EventSource.OPEN || currentState === EventSource.CONNECTING) {
      return;
    }

    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close existing connection if any (might be in error state)
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Create SSE connection
    const gameIdsParam = liveGameIds.join(',');
    const sseUrl = `${baseUrl}/api/live-stats/subscribe?games=${gameIdsParam}`;
    const eventSource = new EventSource(sseUrl);

    eventSource.onopen = () => {
      setIsConnected(true);
    };

    eventSource.onmessage = (event) => {
      // Parse failures during reconnects are common and noisy — drop silently.
      let data: SSEMessage;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      // Anything else (bad shape, throwing setState reducer, throwing settlement
      // callback) is a real bug — report it via onError so it reaches Sentry.
      try {
        // Handle heartbeat
        if (data.type === 'heartbeat') {
          return;
        }

        // Handle connection confirmation
        if (data.type === 'connected') {
          return;
        }

        // Handle settlement complete notification
        if (data.type === 'settlement_complete') {
          if (onSettlementCompleteRef.current) {
            onSettlementCompleteRef.current();
          }
          return;
        }

        // Handle game stats update (gameId is now a string - Odds API ID)
        if (data.gameId && data.stats) {
          setGameStats((prev) => {
            const next = new Map(prev);
            next.set(data.gameId!, data.stats!);
            return next;
          });
        }
      } catch (err) {
        onErrorRef.current?.(err instanceof Error ? err : new Error(String(err)), {
          phase: 'sse-onmessage',
          messageType: data.type ?? null,
          gameId: data.gameId ?? null,
        });
      }
    };

    eventSource.onerror = () => {
      setIsConnected(false);
      eventSource.close();
      eventSourceRef.current = null;

      // Reconnect after 5 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, 5000);
    };

    eventSourceRef.current = eventSource;
  }, [liveGameIds, baseUrl]);

  // Disconnect from SSE
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setIsConnected(false);
  }, []);

  // Connect/disconnect based on game IDs
  useEffect(() => {
    if (liveGameIds.length > 0) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [liveGameIds, connect, disconnect]);

  // Handle visibility change - force reconnect when tab becomes visible
  // Browsers throttle/pause SSE on hidden tabs, so we may miss updates even if readyState says OPEN
  // NOTE: Mobile (React Native) doesn't have document.hidden, so this is web-only
  useEffect(() => {
    // Only set up visibility listener if document is available (web)
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      if (!document.hidden && liveGameIds.length > 0) {
        // Force a fresh reconnect to get latest data — close existing connection first
        // so the readyState guard in connect() doesn't skip it
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connect, liveGameIds]);

  // Connection health check — catches dropped connections from auth cycles,
  // network suspension, or any edge case where the reconnect loop breaks
  // NOTE: Only runs in web environments where EventSource is available
  useEffect(() => {
    if (liveGameIds.length === 0) return;
    if (typeof EventSource === 'undefined') return;

    const healthCheck = setInterval(() => {
      const state = eventSourceRef.current?.readyState;
      if (state !== EventSource.OPEN && state !== EventSource.CONNECTING) {
        connect();
      }
    }, 30_000); // Check every 30s

    return () => clearInterval(healthCheck);
  }, [liveGameIds, connect]);

  // Use shared data callbacks (eliminates duplication with mobile hook)
  const callbacks = useLiveStatsCallbacks(gameStats);

  // Count games with actual live data
  const liveGamesCount = useMemo(() => {
    let count = 0;
    for (const stats of gameStats.values()) {
      if (parseGameStatus(stats.status) === 'live') {
        count++;
      }
    }
    return count;
  }, [gameStats]);

  return {
    ...callbacks,
    isConnected,
    trackedGamesCount: liveGameIds.length,
    liveGamesCount,
  };
}
